import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import {
  Plus,
  Search,
  Package,
  Building2,
  FolderOpen,
  FileText,
  Link2,
  Hash,
  Filter,
  Boxes,
  LayoutGrid,
  List,
} from 'lucide-react';
import { ProductFormDialog } from '@/components/Products/ProductFormDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface Product {
  version?: string;
  mold_id?: string;
  mold?: { name: string; code: string };
  id: string;
  product_number: number;
  name: string;
  reference: string;
  description: string;
  customer_id: string;
  project_id: string;
  document_url: string;
  manual_url: string;
  piece_counter: number;
  package_counter: number;
  specifications: any;
  is_active: boolean;
  customer?: { company_name: string; customer_number: number };
  project?: { name: string; project_number: number };
}

export default function Products() {
  const { userRole } = useAuth();
  const { toast } = useToast();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [customerFilter, setCustomerFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [customers, setCustomers] = useState<any[]>([]);
  const [pendingProductData, setPendingProductData] = useState<any>(null);

  // Recuperar producto pendiente de localStorage (viene de crear plybook)
  useEffect(() => {
    const pendingJson = localStorage.getItem("pending_product_model");
    const newPlybookId = localStorage.getItem("new_plybook_for_product");
    
    if (pendingJson) {
      try {
        const pending = JSON.parse(pendingJson);
        // Solo restaurar si tiene menos de 1 hora
        if (Date.now() - pending.timestamp < 3600000) {
          if (newPlybookId) {
            pending.formData.plybook_id = newPlybookId;
          }
          setPendingProductData(pending);
          setDialogOpen(true); // Abrir diálogo automáticamente
          toast({
            title: "Producto recuperado",
            description: "Continuando con la creación del producto" + (newPlybookId ? " con el plybook recién creado" : ""),
          });
        }
        localStorage.removeItem("pending_product_model");
        if (newPlybookId) localStorage.removeItem("new_plybook_for_product");
      } catch (e) {
        console.error("Error restoring pending product:", e);
      }
    }
  }, []);

  const fetchProducts = async () => {
    try {
      const { data, error } = await api
        .from('product_models')
        .select('*')
        .eq('is_active', true)
        .order('product_number', { ascending: false });

      if (error) throw error;

      // Fetch related data
      const customerIds = [...new Set((data || []).map((p: any) => p.customer_id).filter(Boolean))];
      const projectIds = [...new Set((data || []).map((p: any) => p.project_id).filter(Boolean))];
      const moldIds = [...new Set((data || []).map((p: any) => p.mold_id).filter(Boolean))];

      const [customersRes, projectsRes, moldsRes] = await Promise.all([
        customerIds.length > 0
          ? api.from('customers').select('id,company_name,customer_number').in('id', customerIds)
          : { data: [] },
        projectIds.length > 0
          ? api.from('projects').select('id,name,project_number').in('id', projectIds)
          : { data: [] },
        moldIds.length > 0
          ? api.from('molds').select('id,name,code').in('id', moldIds)
          : { data: [] },
      ]);

      const customersMap = Object.fromEntries((customersRes.data || []).map((c: any) => [c.id, c]));
      const projectsMap = Object.fromEntries((projectsRes.data || []).map((p: any) => [p.id, p]));
      const moldsMap = Object.fromEntries((moldsRes.data || []).map((m: any) => [m.id, m]));

      const enrichedData = (data || []).map((p: any) => ({
        ...p,
        customer: customersMap[p.customer_id],
        project: projectsMap[p.project_id],
        mold: moldsMap[p.mold_id],
      }));

      setProducts(enrichedData);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los productos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    const { data } = await api
      .from('customers')
      .select('id,company_name,customer_number')
      .eq('is_active', true)
      .order('company_name');
    setCustomers(data || []);
  };

  useEffect(() => {
    fetchProducts();
    fetchCustomers();
  }, []);

  const filteredProducts = products.filter(product => {
    const matchesSearch =
      product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.customer?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(product.product_number).includes(searchTerm);

    const matchesCustomer = customerFilter === 'all' || product.customer_id === customerFilter;

    return matchesSearch && matchesCustomer;
  });

  const handleEdit = (product: Product) => {
    setSelectedProduct(product);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setSelectedProduct(null);
    setDialogOpen(true);
  };

  const canManage = userRole === 'admin' || userRole === 'supervisor';

  const stats = {
    total: products.length,
    withProject: products.filter(p => p.project_id).length,
    withDocs: products.filter(p => p.document_url || p.manual_url).length,
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
              Productos
            </h1>
            <p className="text-muted-foreground mt-2">
              Catálogo de modelos de productos
            </p>
          </div>
          {canManage && (
            <Button onClick={handleNew} className="bg-gradient-magma hover:shadow-magma-hover">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Producto
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Productos</p>
                <p className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
                  {stats.total}
                </p>
              </div>
              <Package className="w-8 h-8 text-primary/50" />
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Con Proyecto</p>
                <p className="text-3xl font-bold text-blue-500">{stats.withProject}</p>
              </div>
              <FolderOpen className="w-8 h-8 text-blue-500/50" />
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Con Documentación</p>
                <p className="text-3xl font-bold text-green-500">{stats.withDocs}</p>
              </div>
              <FileText className="w-8 h-8 text-green-500/50" />
            </div>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Buscar productos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={customerFilter} onValueChange={setCustomerFilter}>
            <SelectTrigger className="w-full md:w-64">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Cliente" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los clientes</SelectItem>
              {customers.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  C-{String(c.customer_number).padStart(3, '0')} - {c.company_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex gap-1">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="icon"
              onClick={() => setViewMode('grid')}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'outline'}
              size="icon"
              onClick={() => setViewMode('table')}
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Products */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : filteredProducts.length === 0 ? (
          <Card className="glass-card p-12 text-center">
            <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground">
              {searchTerm || customerFilter !== 'all' ? 'No se encontraron productos' : 'No hay productos registrados'}
            </p>
          </Card>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product) => (
              <Card key={product.id} className="glass-card p-6 hover:shadow-magma transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="font-mono bg-primary/10">
                        PROD-{String(product.product_number).padStart(4, '0')}
                      </Badge>
                    </div>
                    <h3 className="font-semibold text-lg line-clamp-1">{product.name}</h3>
                    {product.reference && (
                      <p className="text-sm text-muted-foreground">
                        Ref: <span className="font-medium">{product.reference}</span>
                      </p>
                    )}
                  </div>
                  <Boxes className="w-8 h-8 text-primary/30" />
                </div>

                <div className="space-y-2 text-sm">
                  {product.customer && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Building2 className="w-4 h-4" />
                      <span className="truncate">
                        C-{String(product.customer.customer_number).padStart(3, '0')} - {product.customer.company_name}
                      </span>
                    </div>
                  )}

                  {product.project && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <FolderOpen className="w-4 h-4" />
                      <span className="truncate">
                        P-{String(product.project.project_number).padStart(4, '0')} - {product.project.name}
                      </span>
                    </div>
                  )}

                  {product.description && (
                    <p className="text-muted-foreground line-clamp-2 mt-2">
                      {product.description}
                    </p>
                  )}

                  <div className="flex gap-2 mt-3">
                    {product.document_url && (
                      <Badge variant="secondary" className="gap-1">
                        <FileText className="w-3 h-3" />
                        Doc
                      </Badge>
                    )}
                    {product.manual_url && (
                      <Badge variant="secondary" className="gap-1">
                        <Link2 className="w-3 h-3" />
                        Manual
                      </Badge>
                    )}
                  </div>
                </div>

                {canManage && (
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => handleEdit(product)}
                    >
                      Editar
                    </Button>
                  </div>
                )}
              </Card>
            ))}
          </div>
        ) : (
          <Card className="glass-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nº</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Referencia</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Proyecto</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Molde</TableHead>
                  <TableHead>Docs</TableHead>
                  {canManage && <TableHead></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">
                        PROD-{String(product.product_number).padStart(4, '0')}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">{product.name}</TableCell>
                    <TableCell>{product.reference || '-'}</TableCell>
                    <TableCell>
                      {product.customer ? (
                        <span className="text-sm">
                          C-{String(product.customer.customer_number).padStart(3, '0')}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {product.project ? (
                        <span className="text-sm">
                          P-{String(product.project.project_number).padStart(4, '0')}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-xs">
                        {product.version || 'V1'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {product.mold ? (
                        <span className="text-sm">{product.mold.code || product.mold.name}</span>
                      ) : (
                        <span className="text-xs text-amber-600">Sin molde</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {product.document_url && <FileText className="w-4 h-4 text-muted-foreground" />}
                        {product.manual_url && <Link2 className="w-4 h-4 text-muted-foreground" />}
                      </div>
                    </TableCell>
                    {canManage && (
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(product)}>
                          Editar
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      <ProductFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        product={selectedProduct}
        pendingData={pendingProductData}
        onSuccess={fetchProducts}
      />
    </DashboardLayout>
  );
}
